import { BadgeIndianRupee, Building2, Layers, Tag } from "lucide-react";

import { useMemo } from "react";
import type { CoachCommercialPrice } from "@flexv-coach/shared";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader, StatsCard } from "@/components/ui/Card";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { ApiError } from "@/lib/api";
import { formatDayMonth } from "@/lib/date";
import { useI18n } from "@/hooks/useI18n";
import { useCoachPricing } from "./useCoachPricing";

/** Label service_type dari core — nilai lain ditampilkan mentah. */
const SERVICE_TYPE_KEYS = ["venue_booking", "venue_booking_add_on", "membership"] as const;

function serviceTypeLabel(t: (key: string) => string, value: string): string {
    const key = `pricing.serviceType.${value}`;
    // Key i18n hanya ada untuk 3 nilai core; selain itu tampilkan mentah.
    return SERVICE_TYPE_KEYS.includes(value as (typeof SERVICE_TYPE_KEYS)[number])
        ? t(key)
        : value;
}

function basisLabel(t: (key: string) => string, value: string | null): string {
    if (!value) return "—";
    return t(`pricing.basis.${value}`) === `pricing.basis.${value}` ? value : t(`pricing.basis.${value}`);
}

const CURRENCY_LOCALES: Record<string, string> = {
    IDR: "id-ID",
    USD: "en-US",
    SGD: "en-SG",
    MYR: "ms-MY",
};

function formatPrice(price: CoachCommercialPrice): string {
    const locale = CURRENCY_LOCALES[price.currencyCode] ?? "en-US";
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: price.currencyCode,
            maximumFractionDigits: price.price % 1 === 0 ? 0 : 2,
        }).format(price.price);
    } catch {
        return `${price.currencyCode} ${price.price}`;
    }
}

function statusTone(status: string): "success" | "neutral" {
    return status === "active" ? "success" : "neutral";
}

export function PricingPage() {
    const { t } = useI18n();
    const { data, isPending, isError, error, refetch } = useCoachPricing();

    const activeCount = useMemo(
        () => (data ?? []).filter((price) => price.status === "active").length,
        [data],
    );
    const outletCount = useMemo(
        () => new Set((data ?? []).map((price) => price.outletId)).size,
        [data],
    );
    const serviceTypeCount = useMemo(
        () => new Set((data ?? []).map((price) => price.serviceType)).size,
        [data],
    );

    return (
        <div className="flex flex-col gap-6">
            <header>
                <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                    {t("pricing.title")}
                </h1>
                <p className="app-shell-text-muted mt-1 text-sm">{t("pricing.subtitle")}</p>
            </header>

            {isPending && <LoadingBlock label={t("pricing.loading")} />}

            {isError && (
                <Card>
                    <ErrorState
                        message={
                            error instanceof ApiError ? error.message : t("pricing.loadFailed")
                        }
                        onRetry={() => void refetch()}
                    />
                </Card>
            )}

            {data && data.length === 0 && (
                <Card>
                    <EmptyState
                        icon={<Tag className="h-6 w-6" />}
                        title={t("pricing.empty.title")}
                        description={t("pricing.empty.description")}
                    />
                </Card>
            )}

            {data && data.length > 0 && (
                <>
                    <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <StatsCard
                            label={t("pricing.stats.outlets")}
                            value={outletCount}
                            icon={<Building2 className="h-5 w-5" />}
                        />
                        <StatsCard
                            label={t("pricing.stats.activePrices")}
                            value={activeCount}
                            icon={<BadgeIndianRupee className="h-5 w-5" />}
                        />
                        <StatsCard
                            label={t("pricing.stats.serviceTypes")}
                            value={serviceTypeCount}
                            icon={<Layers className="h-5 w-5" />}
                        />
                    </section>

                    <p className="app-text-soft text-xs">{t("pricing.readOnlyNote")}</p>

                    <Card>
                        <CardHeader
                            title={t("pricing.title")}
                            description={t("pricing.subtitle")}
                            icon={<Tag className="h-5 w-5" />}
                        />
                        <CardContent noPadding>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="app-text-soft border-b app-shell-border text-xs uppercase tracking-wide">
                                            <th className="px-5 py-3 font-semibold">
                                                {t("pricing.column.outlet")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("pricing.column.sport")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("pricing.column.serviceType")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("pricing.column.basis")}
                                            </th>
                                            <th className="px-5 py-3 text-right font-semibold">
                                                {t("pricing.column.price")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("pricing.column.status")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("pricing.column.effective")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y app-shell-border">
                                        {data.map((price) => (
                                            <tr
                                                key={price.id}
                                                className="transition-colors hover:app-content-subtle-hover"
                                            >
                                                <td className="app-text-primary whitespace-nowrap px-5 py-4 font-medium">
                                                    {price.outlet?.name ?? "—"}
                                                </td>
                                                <td className="app-text-primary px-5 py-4">
                                                    {price.sport?.name ?? "—"}
                                                </td>
                                                <td className="px-5 py-4">
                                                    {serviceTypeLabel(t, price.serviceType)}
                                                </td>
                                                <td className="app-shell-text-muted px-5 py-4">
                                                    {basisLabel(t, price.pricingBasis)}
                                                </td>
                                                <td className="app-text-primary px-5 py-4 text-right font-semibold">
                                                    {formatPrice(price)}
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Badge tone={statusTone(price.status)}>
                                                        {price.status === "active"
                                                            ? t("pricing.status.active")
                                                            : price.status === "inactive"
                                                              ? t("pricing.status.inactive")
                                                              : price.status}
                                                    </Badge>
                                                </td>
                                                <td className="app-shell-text-muted whitespace-nowrap px-5 py-4">
                                                    {formatDayMonth(price.effectiveFrom)}
                                                    {price.effectiveUntil
                                                        ? ` – ${formatDayMonth(price.effectiveUntil)}`
                                                        : " · ∞"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
